import { configureApiClient, createAppStore } from '@saniplus/api-client';

import { IDB_NAME } from './config';
import { queueReducer, type QueueState } from '../features/offline';
import { photoPromptReducer, type PhotoPromptState } from '../features/profile/photoPromptSlice';

/**
 * This app keeps an offline identity snapshot; admin-web does not.
 *
 * A worker reopens the installed app from the home screen inside a plant with
 * no signal, and the refresh cookie cannot be exchanged without a network.
 * Without a snapshot of *who is signed in* — `/auth/me`'s payload, no
 * credential of any kind — that cold start had nothing to render and bounced
 * them to a login screen they cannot use, hiding a punch still queued under
 * their user id (`@saniplus/api-client`'s `auth/sessionSnapshot.ts`).
 *
 * It is set here rather than in `bootstrap.ts` because it belongs to the same
 * decision the store makes: the auth slice is what gets rehydrated, and the
 * `offlineQueue` mirror below is what it exists to keep reachable. It must be
 * in place before the first `bootstrapSession` — this module is imported long
 * before any of them, and `configureApiClient` merges, so `bootstrap.ts`'s own
 * later call leaves it standing.
 */
configureApiClient({ sessionSnapshotDb: IDB_NAME });

/**
 * One store per app, built by the shared factory so the base slice, the auth
 * slice and the RTK Query middleware are wired identically in both.
 *
 * `offlineQueue` is the punch queue's Redux **mirror**, not the queue itself:
 * IndexedDB is the system of record (`features/offline/README.md`), and the
 * mirror exists so the shell can render "1 pointage en attente" without
 * reading a database on every paint. Registering it here is the integrator
 * wiring that feature asks for.
 */
export const store = createAppStore({
  reducers: { offlineQueue: queueReducer, photoPrompt: photoPromptReducer },
});

/*
 * The intersection is not decoration. `createAppStore` types its extra
 * reducers through an index signature, so `offlineQueue` arrives as
 * `[x: string]: unknown` and every queue selector — which asks for
 * `{ offlineQueue: QueueState }` — stops compiling. Naming the slice here is
 * the one-line fix; the alternative is a cast at every call site.
 */
export type RootState = ReturnType<typeof store.getState> & {
  offlineQueue: QueueState;
  /** P-01's session-scoped skip. In memory only — never IndexedDB. */
  photoPrompt: PhotoPromptState;
};
export type AppDispatch = typeof store.dispatch;
